import { Controller } from "@hotwired/stimulus"
import Chartkick from "chartkick"
import "chartkick/chart.js"

const sourceLabels = {
  actual: "Actual", mixed: "Actual + scheduled", scheduled: "Scheduled", projected: "Projected",
  historical: "Historical schedule — excluded from projected cash balance",
  partial: "Scheduled — includes dates before the boundary"
}
const money = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })

// Register on the actual adapter's constructor: Chartkick may resolve its own
// Chart.js copy. The per-chart enabled option protects unrelated charts.
const regionColours = { before: "#f4f5f7", mixed: "#f1eef8", after: "#fff9ed" }
const periodShading = {
  id: "cashflowPeriods",
  beforeDraw(chart, _args, options) {
    if (!options.enabled || !chart.chartArea) return
    const { ctx, chartArea: area, scales: { x } } = chart
    const count = chart.data.labels.length
    const spacing = count > 1 ? x.getPixelForValue(1) - x.getPixelForValue(0) : area.width
    const band = (index, color) => {
      const centre = x.getPixelForValue(index)
      const left = Math.max(area.left, centre - spacing / 2)
      const right = Math.min(area.right, centre + spacing / 2)
      if (color) {
        ctx.fillStyle = color
        ctx.fillRect(left, area.top, right - left, area.height)
      }
      return { left, right }
    }
    ctx.save()
    options.regions.forEach((region, index) => band(index, regionColours[region]))
    options.scenarioIndices.forEach(index => band(index, "rgba(175, 215, 251, 0.38)"))
    if (options.boundaryIndex >= 0) {
      const { left, right } = band(options.boundaryIndex)
      ctx.fillStyle = "#6b64b9"
      ctx.fillRect(left, area.top, right - left, 2)
    }
    ctx.restore()
  }
}

export default class extends Controller {
  static targets = ["flow", "balance", "breakdown"]
  static values = { data: Object }

  connect() {
    this.renderCharts()
  }

  disconnect() {
    this.teardown()
  }

  teardown() {
    this.charts?.forEach(chart => {
      if (!chart.destroyed) chart.destroy()
      const id = chart.getElement().id
      // An outgoing Turbo Frame must not remove a new instance with the same id.
      if (Chartkick.charts[id] === chart) delete Chartkick.charts[id]
    })
    this.charts = []
  }

  renderCharts() {
    this.teardown()
    const data = this.dataValue
    this.chartData = data
    if (this.hasFlowTarget) {
      const series = [data.cashflow]
      if (this.hasBreakdownTarget && this.breakdownTarget.checked) series.push(...data.breakdown)
      this.createChart(this.flowTarget, series)
    }
    if (this.hasBalanceTarget) {
      this.createChart(this.balanceTarget, [data.cash_balance])
    }
  }

  createChart(element, series) {
    const chart = new Chartkick.LineChart(element, series.map(item => this.dataset(item)), this.options())
    this.charts.push(chart)
    const instance = chart.getChartObject()
    const library = instance.constructor
    if (!library.registry.plugins.get(periodShading.id)) library.register(periodShading)
    instance.update("none")
  }

  dataset(series) {
    const points = series.points
    return {
      name: series.name, color: series.color,
      data: points.map(point => [point.period_key, point.amount === null ? null : Number(point.amount)]),
      library: {
        cashflowPoints: points, spanGaps: false, borderWidth: series.key === this.chartData.cashflow.key ? 3 : 2,
        pointRadius: points.map(point => point.source === "mixed" ? 6 : 3),
        pointStyle: points.map(point => point.source === "mixed" ? "rectRot" : "circle"),
        pointBackgroundColor: points.map(point => point.source === "historical" ? "#8a929f" : series.color),
        pointHitRadius: 12,
        segment: {
          borderDash: context => points[context.p0DataIndex].source === "actual" && points[context.p1DataIndex].source === "actual" ? [] : [6, 4],
          borderColor: context => points[context.p0DataIndex].source === "historical" && points[context.p1DataIndex].source === "historical" ? "#8a929f" : series.color
        }
      }
    }
  }

  options() {
    const data = this.chartData
    const periods = data.periods
    return {
      adapter: "chartjs", discrete: true, curve: false, prefix: "£", thousands: ",", legend: "bottom", height: "300px",
      library: {
        animation: false, maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          cashflowPeriods: {
            enabled: true,
            regions: periods.map(period => period.end_date < data.boundary.date ? "before" : period.start_date >= data.boundary.date ? "after" : "mixed"),
            boundaryIndex: periods.findIndex(period => period.key === data.boundary.period_key),
            scenarioIndices: periods.flatMap((period, index) => period.scenario ? [index] : [])
          },
          tooltip: { callbacks: {
            title: items => periods[items[0].dataIndex].label,
            label: context => `${context.dataset.label}: ${money.format(context.parsed.y)} · ${sourceLabels[context.dataset.cashflowPoints[context.dataIndex].source]}`,
            afterBody: items => {
              const scenario = periods[items[0].dataIndex].scenario
              if (!scenario) return ""
              if (scenario.eligibility === "excluded") return "Scenario dates excluded before the forecast boundary"
              if (scenario.eligibility === "partial") return `Scenario applies to scheduled amounts from ${scenario.eligible_from}`
              return "Scenario applies to scheduled amounts"
            }
          } }
        },
        scales: {
          x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, callback: value => periods[value]?.label } },
          y: {
            grid: { color: context => context.tick.value === 0 ? "#536174" : "#eef0f3" },
            ticks: { callback: value => money.format(value) }
          }
        }
      }
    }
  }
}
